'use client'

import { useEffect, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import InstagramIcon from '@/components/InstagramIcon'
import LinkedInIcon from '@/components/LinkedInIcon'
import TikTokIcon from '@/components/TikTokIcon'
import FacebookIcon from '@/components/FacebookIcon'
import { api } from '@/utils/trpc'
import { GenerationRequestStatus } from '@/server/db/schema'

function PlatformIcon({ platform }: { platform: string }) {
  if (platform === 'LINKEDIN') return <LinkedInIcon className="size-3" />
  if (platform === 'TIKTOK') return <TikTokIcon className="size-3" />
  if (platform === 'FACEBOOK') return <FacebookIcon className="size-3" />
  return <InstagramIcon className="size-3" />
}

export default function SocialPostQueuePanel() {
  const t = useTranslations('SocialPostQueuePanel')
  const utils = api.useUtils()

  const query = api.instagramPosts.list.useQuery(
    { statuses: ['PENDING', 'GENERATING', 'ERROR'], pageSize: 50 },
    { refetchInterval: 3000 },
  )

  const items = query.data?.items ?? []
  const prevIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const currentIds = new Set(items.map((item) => item.id))
    const prevIds = prevIdsRef.current
    const anyDisappeared = [...prevIds].some((id) => !currentIds.has(id))
    if (anyDisappeared && prevIds.size > 0) {
      utils.instagramPosts.list.invalidate()
    }
    prevIdsRef.current = currentIds
  }, [items, utils])

  const deleteMutation = api.instagramPosts.delete.useMutation({
    onSuccess: () => {
      utils.instagramPosts.list.invalidate()
    },
  })

  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
        {t('heading')}
      </span>
      <div className="flex gap-3 overflow-x-auto pb-1 custom-scrollbar">
        {items.map((item) => {
          const firstAsset = item.assets?.[0]
          const thumbUrl = firstAsset?.thumbnailUrl ?? firstAsset?.url

          return (
            <div
              key={item.id}
              className="relative flex shrink-0 flex-col gap-2 rounded-2xl bg-input/40 p-2.5 ring-1 ring-foreground/5 w-36"
            >
              <div className="relative size-12 overflow-hidden rounded-lg bg-input/60">
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt={firstAsset?.title ?? ''}
                    className="absolute inset-0 size-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 animate-pulse bg-input/80" />
                )}
                {/* Platform icon overlay */}
                <div className="absolute bottom-0.5 right-0.5 flex items-center justify-center size-4 rounded-full bg-background/80 ring-1 ring-foreground/10">
                  <PlatformIcon platform={item.platform} />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <StatusBadge status={item.status} t={t} />
                {item.retryCount > 0 && (
                  <span className="text-[10px] text-muted-foreground/70">
                    {t('retryCount', { count: item.retryCount })}
                  </span>
                )}
                {item.status === 'ERROR' && item.errorMessage && (
                  <span className="text-[10px] text-destructive leading-snug line-clamp-2">
                    {item.errorMessage}
                  </span>
                )}
              </div>

              <button
                type="button"
                aria-label={t('delete')}
                onClick={() => deleteMutation.mutate({ id: item.id })}
                disabled={deleteMutation.isPending}
                className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-background/80 ring-1 ring-foreground/10 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                <X className="size-3" />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StatusBadge({
  status,
  t,
}: {
  status: GenerationRequestStatus
  t: ReturnType<typeof useTranslations<'SocialPostQueuePanel'>>
}) {
  if (status === 'PENDING') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        {t('statusPending')}
      </span>
    )
  }
  if (status === 'GENERATING') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-500">
        <Loader2 className="size-2.5 animate-spin" />
        {t('statusGenerating')}
      </span>
    )
  }
  if (status === 'ERROR') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
        {t('statusError')}
      </span>
    )
  }
  return null
}
